/**
 * Pick & Mix — EOY giftbox showcase ("Gift a moment").
 *
 * - A row selects that box: the image shows the closed box, then animates to
 *   its contents (no contents image → the closed box stays). Sold-out rows
 *   select the same way.
 * - The list scrolls inside the block; the "Scroll voor …" label goes once
 *   the visitor scrolls it, the fade once the end is reached.
 * - "Ontdek de giftboxen" and the "i" on the image dispatch
 *   `pb:pick-mix:giftbox-open` with the selected (or first) row's key, for
 *   the giftbox pop-up.
 * - "Shop alle cadeaus" switches to the Gifts tab when the page has one
 *   (brand tab `gifts`), otherwise it follows its link to the collection.
 *
 * Markup: snippets/pb-pick-mix-giftbox.liquid.
 */
(() => {
  // How long the closed box stays in view, counted from the moment both images are loaded.
  const OPEN_DELAY = 1200;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Resolves once the image has loaded (or failed, so a broken file never blocks the animation).
  const whenLoaded = (img) => {
    if (!img || (img.complete && img.naturalWidth > 0)) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  };

  // Lazy images inside a box that is display:none do not load until asked.
  const loadBox = (box) => {
    box?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  };

  function initShowcase(el) {
    if (el.dataset.gbxReady === 'true') return;
    el.dataset.gbxReady = 'true';

    const rows = [...el.querySelectorAll('[data-gbx-row]')];
    const mood = el.querySelector('[data-gbx-mood]');
    const info = el.querySelector('.pb-pm-gbx__info');
    const list = el.querySelector('[data-gbx-list]');
    let selected = null;
    let openTimer = 0;

    const select = (row) => {
      const key = row.dataset.gbxRow;
      if (key === selected) return;
      selected = key;
      clearTimeout(openTimer);

      for (const other of rows) other.setAttribute('aria-pressed', other === row ? 'true' : 'false');
      if (mood) mood.hidden = true;

      let box = null;
      for (const candidate of el.querySelectorAll('[data-gbx-box]')) {
        const match = candidate.dataset.gbxBox === key;
        candidate.hidden = !match;
        candidate.classList.remove('is-open');
        if (match) box = candidate;
      }

      if (info) {
        info.hidden = false;
        const name = row.querySelector('.pb-pm-gbx__row-name')?.textContent.trim();
        if (name) info.setAttribute('aria-label', `${info.dataset.label || info.getAttribute('aria-label')}: ${name}`);
      }

      if (!box) return;
      loadBox(box);

      const closedImg = box.querySelector('.pb-pm-gbx__box-img--closed');
      const openImg = box.querySelector('.pb-pm-gbx__box-img--open');

      // The closed box fades in once it has loaded (is-ready); the explode
      // starts OPEN_DELAY after both images are there, so it never runs
      // before the visitor has seen the closed box. A newer selection wins.
      whenLoaded(closedImg).then(() => {
        if (selected !== key) return;
        box.classList.add('is-ready');

        whenLoaded(openImg).then(() => {
          if (selected !== key) return;
          if (reducedMotion()) {
            box.classList.add('is-open');
            return;
          }
          openTimer = setTimeout(() => {
            if (selected === key) box.classList.add('is-open');
          }, OPEN_DELAY);
        });
      });
    };

    // Start loading a box's images as soon as the visitor points at or focuses its row.
    for (const row of rows) {
      const warm = () => loadBox(el.querySelector(`[data-gbx-box="${row.dataset.gbxRow}"]`));
      row.addEventListener('pointerenter', warm, { once: true });
      row.addEventListener('focus', warm, { once: true });
    }

    if (info) info.dataset.label = info.getAttribute('aria-label') || '';

    for (const row of rows) row.addEventListener('click', () => select(row));

    el.querySelectorAll('[data-gbx-open]').forEach((button) => {
      button.addEventListener('click', () => {
        el.dispatchEvent(
          new CustomEvent('pb:pick-mix:giftbox-open', {
            bubbles: true,
            detail: { key: selected || rows[0]?.dataset.gbxRow || null, trigger: button },
          }),
        );
      });
    });

    el.querySelector('[data-gbx-gifts]')?.addEventListener('click', (event) => {
      const root = el.closest('[data-pb-pick-mix]');
      const giftsTab = root?.querySelector('[data-pm-brand-tab="gifts"]');
      const filter = window.PBPickMixPlanetBFilter;
      if (!root || !giftsTab || !filter?.switchBrand) return;
      event.preventDefault();
      filter.switchBrand(root, 'gifts');
    });

    if (list) {
      const sync = () => {
        el.classList.toggle('is-list-scrolled', list.scrollTop > 4);
        el.classList.toggle('is-list-end', list.scrollTop + list.clientHeight >= list.scrollHeight - 4);
      };
      list.addEventListener('scroll', sync, { passive: true });
      sync();
    }
  }

  /**
   * Tab layout (snippets/pb-pick-mix-giftbox-tabs.liquid). Collapsed on load;
   * a tab (or the hint, = first tab) opens its panel, the arrow collapses it.
   * "+ Toevoegen" is handled by the pop-up controller (it owns addItem); the
   * eye and "Ontdek de giftbox →" open the pop-up at the selected product.
   */
  function initTabs(el) {
    if (el.dataset.gbxReady === 'true') return;
    el.dataset.gbxReady = 'true';

    const tabs = [...el.querySelectorAll('[data-gbx-row]')];
    const strip = el.querySelector('[data-gbx-tabs]');
    const panels = el.querySelector('[data-gbx-panels]');
    const toggle = el.querySelector('[data-gbx-collapse]');
    let selected = null;

    // A selected tab over the panel's rounded corner (20px) drops its foot there and squares that corner (CSS).
    const updateEdge = () => {
      const tab = tabs.find((t) => t.dataset.gbxRow === selected);
      const edge = [];
      if (tab && !panels.hidden) {
        const t = tab.getBoundingClientRect();
        const p = panels.getBoundingClientRect();
        if (t.left < p.left + 20) edge.push('start');
        if (t.right > p.right - 20) edge.push('end');
      }
      if (edge.length) el.dataset.gbxEdge = edge.join(' ');
      else delete el.dataset.gbxEdge;
    };
    let edgeFrame = 0;
    const queueEdge = () => {
      cancelAnimationFrame(edgeFrame);
      edgeFrame = requestAnimationFrame(updateEdge);
    };
    strip?.addEventListener('scroll', queueEdge, { passive: true });
    window.addEventListener('resize', queueEdge);

    const select = (tab) => {
      selected = tab.dataset.gbxRow;
      for (const other of tabs) other.setAttribute('aria-pressed', other === tab ? 'true' : 'false');
      el.querySelectorAll('[data-gbx-panel-inline]').forEach((panel) => {
        panel.hidden = panel.dataset.gbxPanelInline !== selected;
        if (!panel.hidden) loadBox(panel);
      });
      panels.hidden = false;
      if (toggle) toggle.hidden = false;
      el.classList.add('is-open');

      // Keep the tab fully in view inside the (mobile) scroll strip without scrolling the page.
      const t = tab.getBoundingClientRect();
      const s = strip.getBoundingClientRect();
      const shift = t.left < s.left ? t.left - s.left : Math.max(0, t.right - s.right);
      if (shift) strip.scrollBy({ left: shift, behavior: reducedMotion() ? 'auto' : 'smooth' });
      updateEdge();
    };

    const collapse = () => {
      const last = tabs.find((tab) => tab.dataset.gbxRow === selected);
      selected = null;
      for (const tab of tabs) tab.setAttribute('aria-pressed', 'false');
      panels.hidden = true;
      if (toggle) toggle.hidden = true;
      el.classList.remove('is-open');
      updateEdge();
      // The mobile arrow sits below the panel: bring the (now short) block back into view.
      if (el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start', behavior: 'auto' });
      last?.focus({ preventScroll: true });
    };

    for (const tab of tabs) {
      tab.addEventListener('click', () => select(tab));
      const warm = () => loadBox(el.querySelector(`[data-gbx-panel-inline="${tab.dataset.gbxRow}"]`));
      tab.addEventListener('pointerenter', warm, { once: true });
      tab.addEventListener('focus', warm, { once: true });
    }
    el.querySelector('[data-gbx-first]')?.addEventListener('click', () => tabs[0] && select(tabs[0]));
    toggle?.addEventListener('click', collapse);

    el.querySelectorAll('[data-gbx-open]').forEach((button) => {
      button.addEventListener('click', () => {
        el.dispatchEvent(
          new CustomEvent('pb:pick-mix:giftbox-open', {
            bubbles: true,
            detail: { key: selected || tabs[0]?.dataset.gbxRow || null, trigger: button },
          }),
        );
      });
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-pm-gbx]').forEach(initShowcase);
    scope.querySelectorAll('[data-pm-gbx-tabs]').forEach(initTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /**
   * The giftbox pop-up (`data-pm-gbx-popup="giftbox"`, panels keyed by showcase
   * slot). `addItem(card)` is the section's own add function, so price, tier
   * and basket stay identical to a click on a grid card. Opens on
   * `pb:pick-mix:giftbox-open` (showcase button / "i") at `detail.key`, or from
   * a giftbox card in the grid; "Volgende →" and a horizontal swipe on the
   * photo go through the products in the order of the showcase rows.
   *
   * @returns {{ open: (key: string, trigger?: Element, strict?: boolean) => boolean }}
   */
  function initPopup(popup, { root, addItem }) {
    if (popup.gbxController) return popup.gbxController;

    const dialog = popup.querySelector('[data-gbx-dialog]');
    const panels = [...popup.querySelectorAll('[data-gbx-panel]')];
    const popupStatus = popup.querySelector('[data-gbx-status]');
    const panelFor = (key) => panels.find((panel) => panel.dataset.gbxPanel === String(key));

    // Same order as the rows (sold out last); fall back to the panel order.
    const order = () => {
      const keys = [...root.querySelectorAll('[data-gbx-row]')].map((row) => row.dataset.gbxRow);
      return keys.length ? keys.filter((key) => panelFor(key)) : panels.map((panel) => panel.dataset.gbxPanel);
    };

    let activeKey = null;
    let restoreFocusTo = null;

    const showImage = (panel, index) => {
      panel.querySelectorAll('.pb-pm-gbx-pop__main-img').forEach((img, i) => {
        img.classList.toggle('is-active', i === index);
        if (i === index) img.loading = 'eager';
      });
      panel.querySelectorAll('[data-gbx-thumb]').forEach((thumb) => {
        thumb.setAttribute('aria-pressed', Number(thumb.dataset.gbxThumb) === index ? 'true' : 'false');
      });
    };

    const selectTab = (panel, name) => {
      panel.querySelectorAll('[data-gbx-tab]').forEach((tab) => {
        const selected = tab.dataset.gbxTab === name;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      panel.querySelectorAll('[data-gbx-tabpanel]').forEach((tabpanel) => {
        tabpanel.hidden = tabpanel.dataset.gbxTabpanel !== name;
      });
    };

    const showPanel = (key) => {
      const panel = panelFor(key);
      if (!panel) return;
      activeKey = String(key);

      for (const other of panels) other.hidden = other !== panel;
      panel.scrollTop = 0;
      panel.querySelector('.pb-pm-gbx-pop__body')?.scrollTo(0, 0);
      showImage(panel, 0);
      const firstTab = panel.querySelector('[data-gbx-tab]');
      if (firstTab) selectTab(panel, firstTab.dataset.gbxTab);

      const title = panel.querySelector('.pb-pm-gbx-pop__title');
      if (title?.id) dialog.setAttribute('aria-labelledby', title.id);
    };

    const setOpen = (open) => {
      popup.hidden = !open;
      popup.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.classList.toggle('pbpm-gbx-open', open);
      document.body.classList.toggle('pbpm-gbx-open', open);

      if (open) {
        window.PBOverlayStack?.push(popup);
        setTimeout(() => popup.querySelector('.pb-pm-gbx-pop__close')?.focus({ preventScroll: true }), 0);
        return;
      }

      window.PBOverlayStack?.pop(popup);
      if (restoreFocusTo instanceof HTMLElement && restoreFocusTo.isConnected) {
        restoreFocusTo.focus({ preventScroll: true });
      }
      restoreFocusTo = null;
    };

    // strict: only open on that exact panel (a card click); otherwise fall back to the first.
    const open = (key, trigger, strict = false) => {
      const keys = order();
      const known = key != null && keys.includes(String(key));
      if (strict && !known) return false;
      const target = known ? key : keys[0];
      if (target == null) return false;
      restoreFocusTo = trigger || document.activeElement;
      showPanel(target);
      setOpen(true);
      return true;
    };

    const step = (delta) => {
      const keys = order();
      if (keys.length < 2) return;
      const index = keys.indexOf(activeKey);
      showPanel(keys[(index + delta + keys.length) % keys.length]);
    };

    // From the pop-up or from a panel of the tab layout (that one has its own status).
    const add = (button) => {
      const key = CSS.escape(button.dataset.gbxAdd || '');
      const card = popup.querySelector(`[data-gbx-card="${key}"] [data-pm-card]`);
      if (!card || card.dataset.available !== 'true' || button.disabled) return;
      addItem(card);

      const status = button.closest('[data-pm-gbx-tabs]')?.querySelector('[data-gbx-status]') || popupStatus;
      const label = button.querySelector('[data-gbx-add-label]');
      if (label) label.textContent = button.dataset.addedLabel || button.dataset.label || '';
      if (status) status.textContent = button.dataset.addedLabel || '';
      clearTimeout(Number(button.dataset.gbxTimer));
      button.dataset.gbxTimer = String(
        setTimeout(() => {
          if (label) label.textContent = button.dataset.label || '';
          if (status) status.textContent = '';
        }, 2000),
      );
    };

    root.addEventListener('pb:pick-mix:giftbox-open', (event) => {
      open(event.detail?.key, event.detail?.trigger);
    });

    root.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-pm-gbx-tabs] [data-gbx-add]') : null;
      if (button) add(button);
    });

    popup.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-gbx-close]')) return setOpen(false);
      if (target.closest('[data-gbx-next]')) return step(1);

      const thumb = target.closest('[data-gbx-thumb]');
      if (thumb) return showImage(thumb.closest('[data-gbx-panel]'), Number(thumb.dataset.gbxThumb));

      const tab = target.closest('[data-gbx-tab]');
      if (tab) return selectTab(tab.closest('[data-gbx-panel]'), tab.dataset.gbxTab);

      const addButton = target.closest('[data-gbx-add]');
      if (addButton) add(addButton);
    });

    // Swipe the photo left/right to go to the next/previous box.
    let swipeStart = null;
    popup.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || !event.target.closest('[data-gbx-swipe]')) return;
      swipeStart = { x: event.clientX, y: event.clientY };
    });
    popup.addEventListener('pointerup', (event) => {
      if (!swipeStart) return;
      const dx = event.clientX - swipeStart.x;
      const dy = event.clientY - swipeStart.y;
      swipeStart = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
    });
    popup.addEventListener('pointercancel', () => (swipeStart = null));

    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        return;
      }

      const tab = event.target.closest?.('[data-gbx-tab]');
      if (tab && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        const tabs = [...tab.parentElement.querySelectorAll('[data-gbx-tab]')];
        const next = tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        selectTab(tab.closest('[data-gbx-panel]'), next.dataset.gbxTab);
        next.focus();
        event.preventDefault();
        return;
      }

      if (event.key !== 'Tab') return;
      // aria-modal hides the page from screen readers; keep the keyboard in too.
      const items = [...dialog.querySelectorAll('button:not([disabled]), [href], [tabindex="0"]')].filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0,
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    popup.gbxController = { open };
    return popup.gbxController;
  }

  /**
   * Called from the section's inline script, which owns the basket. Returns
   * `openFromCard(card, trigger)` for the grid: giftbox/kerstbal cards open the
   * giftbox pop-up at their slot. Returns false for every other card (Cadeautip
   * cards included), so the regular product popup opens.
   *
   * @param {{ root: HTMLElement, addItem: (card: HTMLElement) => void }} options
   */
  function initPopups({ root, addItem } = {}) {
    const popups = {};
    root?.querySelectorAll('[data-pm-gbx-popup]').forEach((popup) => {
      popups[popup.dataset.pmGbxPopup] = initPopup(popup, { root, addItem });
    });

    return {
      openFromCard(card, trigger) {
        if (card.dataset.pmGift === 'box') return popups.giftbox?.open(card.dataset.pmGiftBox, trigger, true) || false;
        return false;
      },
    };
  }

  window.PBPickMixGiftbox = { init: initPopups };
})();
